import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch, } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import giardinoImg from '@/assets/destinations/giardino.jpg';
import productsImg from '@/assets/destinations/products.jpg';
import tourismImg from '@/assets/destinations/tourism.jpg';
import { setHead } from '@/composables/useHead';
import { localizedTo } from '@/composables/useLocalizedRoute';
import AlbumViewer from './AlbumViewer.vue';
const META_BY_CATEGORY = {
    all: { i18nKey: 'seo.portfolio', ogPath: '/og-image.jpg', canonical: '/portfolio' },
    hotels: { i18nKey: 'seo.hotels', ogPath: '/portfolio/giardino-lago/15.jpg', canonical: '/portfolio/hotels' },
    tourism: { i18nKey: 'seo.tourism', ogPath: '/portfolio/lucerne/1.jpg', canonical: '/portfolio/tourism' },
    products: { i18nKey: 'seo.products', ogPath: '/portfolio/veoli-botanica/1.jpg', canonical: '/portfolio/products' },
};
export default defineComponent({
    name: 'PortfolioGallery',
    components: { AlbumViewer },
    setup() {
        const { tm, t } = useI18n();
        const route = useRoute();
        const albums = computed(() => tm('portfolio.albums'));
        const isCategory = (v) => (v === 'hotels' || v === 'tourism' || v === 'products');
        const readCategory = () => {
            const p = route.params.category;
            const v = Array.isArray(p) ? p[0] : p;
            return isCategory(v) ? v : null;
        };
        const selectedCategory = ref(readCategory());
        const albumIndex = ref(null);
        const viewerStart = ref(null);
        const applyHead = (cat) => {
            const m = META_BY_CATEGORY[cat ?? 'all'];
            setHead({
                title: t(`${m.i18nKey}.title`),
                description: t(`${m.i18nKey}.description`),
                basePath: m.canonical,
                ogImage: `https://www.travellingdominika.com${m.ogPath}`,
            });
        };
        onMounted(() => applyHead(selectedCategory.value));
        watch(() => route.params.category, () => {
            const next = readCategory();
            if (selectedCategory.value !== next) {
                selectedCategory.value = next;
                albumIndex.value = null;
                viewerStart.value = null;
                applyHead(next);
            }
        });
        const filteredAlbums = computed(() => {
            if (selectedCategory.value === null)
                return [];
            return albums.value
                .map((album, idx) => ({ album, flatIndex: idx }))
                .filter(({ album }) => album.category === selectedCategory.value);
        });
        const categories = computed(() => [
            {
                value: 'hotels',
                label: t('portfolio.categoryLabels.hotels'),
                tagline: t('portfolio.categoryTaglines.hotels'),
                image: giardinoImg,
                delay: 0,
            },
            {
                value: 'tourism',
                label: t('portfolio.categoryLabels.tourism'),
                tagline: t('portfolio.categoryTaglines.tourism'),
                image: tourismImg,
                delay: 80,
            },
            {
                value: 'products',
                label: t('portfolio.categoryLabels.products'),
                tagline: t('portfolio.categoryTaglines.products'),
                image: productsImg,
                delay: 160,
            },
        ]);
        const activeCategoryLabel = computed(() => (selectedCategory.value
            ? t(`portfolio.categoryLabels.${selectedCategory.value}`)
            : ''));
        const activeAlbum = computed(() => (albumIndex.value !== null ? albums.value[albumIndex.value] : null));
        const openAlbum = (idx) => {
            albumIndex.value = idx;
            viewerStart.value = null;
            window.scrollTo({ top: 0, behavior: 'instant' });
        };
        const backToList = () => {
            albumIndex.value = null;
            viewerStart.value = null;
        };
        const openPhoto = (photoIdx) => {
            viewerStart.value = photoIdx;
        };
        const closeViewer = () => {
            viewerStart.value = null;
        };
        const computeSpan = (tile, img) => {
            if (!img.naturalWidth || !img.naturalHeight)
                return;
            const GRID_ROW = 8;
            const grid = tile.parentElement;
            const gap = grid ? parseFloat(getComputedStyle(grid).rowGap || '0') : 4;
            const tileWidth = tile.getBoundingClientRect().width;
            const renderedHeight = (img.naturalHeight / img.naturalWidth) * tileWidth;
            const span = Math.max(1, Math.round((renderedHeight + gap) / (GRID_ROW + gap)));
            tile.style.gridRowEnd = `span ${span}`;
        };
        const onTileLoad = (e) => {
            const img = e.target;
            const tile = img.closest('.album-page__tile');
            if (!tile)
                return;
            computeSpan(tile, img);
        };
        const recomputeAllSpans = () => {
            const tiles = document.querySelectorAll('.album-page__tile');
            tiles.forEach((tile) => {
                const img = tile.querySelector('img');
                if (img && img.complete)
                    computeSpan(tile, img);
            });
        };
        let resizeTimer = null;
        const onResize = () => {
            if (resizeTimer !== null)
                window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(recomputeAllSpans, 80);
        };
        onMounted(() => {
            window.addEventListener('resize', onResize);
        });
        onBeforeUnmount(() => {
            window.removeEventListener('resize', onResize);
            if (resizeTimer !== null)
                window.clearTimeout(resizeTimer);
        });
        return {
            albums,
            filteredAlbums,
            selectedCategory,
            activeCategoryLabel,
            categories,
            albumIndex,
            activeAlbum,
            viewerStart,
            openAlbum,
            backToList,
            openPhoto,
            closeViewer,
            onTileLoad,
            localizedTo,
        };
    },
});
